import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Check, LockIcon, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { CheckCircleIcon, CheckIcon, ClockIcon, QuestionIcon, TargetIcon, XIcon } from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';
import SyllabusModal from '../../components/modals/SyllabusModal';
import TestListingSort from '../../components/dropdown/TestListingSort';
import { debounce } from '../../lib/debounce';
import formatDateTime from './formatTime';
import TestListingLoader from '../../components/loaders/TestListingLoader';
import toast from 'react-hot-toast';

const filterOptions = [
  { id: 'All', label: 'All' },
  { id: 'Not Attempted', label: 'Not Attempted' },
  { id: 'Attempted', label: 'Attempted' },
  { id: 'Upcoming', label: 'Upcoming' },
  { id: 'Resume', label: 'Resume' },
];

function NonChapterwiseTests({
  tests,
  chapterFilters,
  currentPage,
  totalPages,
  selectedCategoryData,
  handleChapterFilterChange,
  handleClearChapterFilters,
  handlePageChange,
  availableFilters,
  isPYQCategory,
  isPackPurchased,
  loading,
  banners,
}) {
  const navigate = useNavigate();
  const params = useParams();
  const packId = params.packId;

  const [showSyllabusModal, setShowSyllabusModal] = useState(false);
  const [syllabusModalContent, setSyllabusModalContent] = useState({ title: '', syllabus: '' });
  const [localSearch, setLocalSearch] = useState(chapterFilters.search || '');
  const [selectedSortOption, setSelectedSortOption] = useState(null);

  // Scroll mask state
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: true,
  });
  const scrollRef = useRef(null);

  const handleViewSyllabus = (title, syllabusHtml) => {
    setSyllabusModalContent({ title, syllabus: syllabusHtml });
    setShowSyllabusModal(true);
  };

  useEffect(() => {
    checkScrollState();
  }, []);

  const checkScrollState = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setScrollState({
        canScrollLeft: scrollLeft > 0,
        canScrollRight: scrollLeft < scrollWidth - clientWidth - 1,
      });
    }
  };

  const scrollFilters = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      const newScrollLeft =
        direction === 'left'
          ? scrollRef.current.scrollLeft - scrollAmount
          : scrollRef.current.scrollLeft + scrollAmount;

      scrollRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const getMaskStyle = () => {
    const { canScrollLeft, canScrollRight } = scrollState;

    if (!canScrollLeft && !canScrollRight) {
      return {};
    } else if (!canScrollLeft && canScrollRight) {
      return {
        maskImage: 'linear-gradient(to right, black 90%, transparent)',
        WebkitMaskImage: 'linear-gradient(to right, black 90%, transparent)',
      };
    } else if (canScrollLeft && !canScrollRight) {
      return {
        maskImage: 'linear-gradient(to right, transparent, black 10%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%)',
      };
    } else {
      return {
        maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
      };
    }
  };

  useEffect(() => {
    setLocalSearch(chapterFilters.search || '');
  }, [chapterFilters.search]);

  useEffect(() => {
    handleChapterFilterChange({ sorting: selectedSortOption });
  }, [selectedSortOption]);

  const debouncedFilterChange = useMemo(
    () =>
      debounce((searchValue) => {
        handleChapterFilterChange({ search: searchValue });
      }, 400),
    [handleChapterFilterChange],
  );

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    debouncedFilterChange(value);
  };

  const showUpcomingTestToast = (test) => {
    toast.custom(
      (t) => (
        <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg p-4 shadow-lg max-w-md">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ClockIcon className="text-[var(--q3-accent-normal)]" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-[var(--q3-accent-normal)]">
                  {test.title} will be available on {formatDateTime(test?.startDate)}
                </p>
              </div>
            </div>
            <div className="ml-4 flex-shrink-0 cursor-pointer">
              <Button
                variant={'outline'}
                className="inline-flex !bg-[var(--q3-surface-default)] shadow-none border-none text-[var(--q3-danger-normal)] hover:text-[var(--q3-danger-dark)]"
                onClick={(e) => {
                  e.stopPropagation();
                  toast.remove(t.id);
                }}
              >
                <span className="sr-only">Close</span>
                <XIcon />
              </Button>
            </div>
          </div>
        </div>
      ),
      {
        position: 'bottom-center',
        duration: 3000,
      },
    );
  };

  const handleTestNavigation = (testId, path) => {
    const url = `/${packId}/${testId}/${path}?source=test_listing&categoryId=${selectedCategoryData?.groupId || selectedCategoryData?.title}`;
    navigate(url, {
      state: { selectedCategoryId: selectedCategoryData?.groupId },
    });
  };

  const showTestEndedToast = (test) => {
    toast.custom(
      (t) => (
        <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg p-4 shadow-lg max-w-md">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ClockIcon className="text-[var(--q3-accent-normal)]" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-[var(--q3-accent-normal)]">
                  {test.title} has ended on {formatDateTime(test?.endDate)}
                </p>
              </div>
            </div>
            <div className="ml-4 flex-shrink-0 cursor-pointer">
              <Button
                variant={'outline'}
                className="inline-flex !bg-[var(--q3-surface-default)] shadow-none border-none text-[var(--q3-danger-normal)] hover:text-[var(--q3-danger-dark)]"
                onClick={(e) => {
                  e.stopPropagation();
                  toast.remove(t.id);
                }}
              >
                <span className="sr-only">Close</span>
                <XIcon />
              </Button>
            </div>
          </div>
        </div>
      ),
      {
        position: 'bottom-center',
        duration: 3000,
      },
    );
  };

  const EmptyTests = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-4">
        <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-2">😕 No tests found</h3>
        <p className="text-[var(--q3-neutral-light)] mb-6">Try adjusting your search or filter criteria</p>
      </div>
      <Button
        onClick={() => {
          handleClearChapterFilters();
          setSelectedSortOption(null);
        }}
        className="rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] transition-colors duration-300"
      >
        Clear Filters
      </Button>
    </div>
  );

  return (
    <div className="max-w-[1000px] mx-auto text-center md:pr-3">
      <div className="q3-glass p-4 pt-0 -mt-1 -mx-4 md:-mx-4 lg:-mx-4 rounded-xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <h2 className="text-2xl font-bold">{selectedCategoryData?.title}</h2>
          <div className="relative w-full md:w-56 mt-2 md:mt-0 bg-[var(--q3-surface-glass)] border-[var(--q3-stroke-normal)] rounded-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search Tests"
              className="pl-10 rounded-full"
              value={localSearch}
              onChange={handleSearchInput}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4 mb-2 overflow-x-auto">
          <div className="relative">
            <div
              ref={scrollRef}
              className="flex gap-2 rounded-[12px] overflow-x-auto overflow-y-hidden whitespace-nowrap relative"
              style={getMaskStyle()}
              onScroll={checkScrollState}
            >
              <div className="flex gap-2 flex-shrink-0">
                <TestListingSort
                  setSelectedSortOption={setSelectedSortOption}
                  selectedSortOption={selectedSortOption}
                />
                <div className="border border-l-[1px] border-[var(--q3-stroke-normal)]"></div>

                {filterOptions?.length > 0 && (
                  <>
                    {filterOptions.map((status) => (
                      <Button
                        key={status.id}
                        variant={filterOptions.id === status ? 'default' : ''}
                        className={`rounded-full capitalize flex-shrink-0 ${chapterFilters.testStatus === status.id ? 'bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)]' : 'bg-[var(--q3-surface-glass)] hover:bg-[var(--q3-accent-light)] text-[var(--q3-neutral-default)] border border-[var(--q3-stroke-normal)]'}`}
                        onClick={() => {
                          handleChapterFilterChange({
                            testStatus: filterOptions.testStatus === status.id ? null : status.id,
                          });
                        }}
                      >
                        {status.label}
                        {chapterFilters.testStatus === status.id && <Check className="ml-1 h-3 w-3" />}
                      </Button>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* {availableFilters && availableFilters.selectUnit && availableFilters.selectUnit.length > 2 && (
            <Select value={chapterFilters.selectUnit || 'All'}>
              <SelectTrigger className="w-48 rounded-full">
                <SelectValue placeholder="Select Unit" />
              </SelectTrigger>
              <SelectContent>
                {availableFilters.selectUnit.map((unit) => (
                  <SelectItem key={unit.unit} value={unit.unit}>
                    {unit.unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )} */}
        </div>
      </div>
      {/* <div
        className="text-start bg-[var(--q3-surface-dim)] border-l-2 border-[var(--q3-accent-normal)] p-4 rounded-r-lg mb-6 flex flex-col lg:flex-row lg:items-center cursor-pointer gap-4"
        onClick={() => navigate(`/v2/${packId}/analysis/performance`)}
      >
        <div className="flex items-center flex-1">
          <PresentationChartIcon className="size-16 lg:size-8 mx-2 text-[var(--q3-accent-normal)]" />
          <span className="px-2">
            Check out the JEE Main 2025 Test Analysis to evaluate your performance more effectively.
          </span>
        </div>

        <div className="flex justify-center lg:justify-end">
          <button className="relative inline-flex h-12 min-w-fit overflow-hidden rounded-full p-[3px] focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50 whitespace-nowrap cursor-pointer">
            <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,var(--q3-accent-normal)_0%,var(--q3-danger-light)_50%,var(--q3-accent-normal)_100%)]" />
            <span className="inline-flex h-full w-full items-center justify-center rounded-full bg-[var(--q3-surface-dim)] px-6 py-1 text-sm font-medium text-[var(--q3-accent-normal)] backdrop-blur-3xl whitespace-nowrap">
              View Pack Analysis
            </span>
          </button>
        </div>
      </div> */}
      <div className="relative z-0">
        {banners &&
          banners.length > 0 &&
          banners
            .filter((banner) => {
              return (
                !banner.groupIds ||
                banner.groupIds?.length === 0 ||
                banner.groupIds.includes(selectedCategoryData?.groupId)
              );
            })
            .map((banner, index) => (
              <div
                key={index}
                className={`bg-[${banner.bgColor}] border-l-2 border-[${banner.borderColor}] text-[${banner.textColor}] p-4 rounded-r-lg mb-6 flex items-center justify-between`}
              >
                <div className="flex-1 text-left">{banner.content}</div>
                {banner.isButtonEnabled && (
                  <div className="ml-4">
                    <Button
                      className={`bg-[${banner.buttonBgColor}] text-[${banner.buttonTextColor}] hover:bg-[${banner.buttonBgColor}]`}
                      onClick={() => window.open(banner.url, '_blank')}
                    >
                      {banner.buttonText}
                    </Button>
                  </div>
                )}
              </div>
            ))}
        {loading ? (
          <TestListingLoader />
        ) : (
          <div className="space-y-8 py-3">
            {tests.length > 0 ? (
              tests.map((testData, index) => {
                const test = testData.test;
                const isReleased = new Date() >= new Date(test.startDate);
                let status;

                if (!isReleased) {
                  status = 'not-released';
                } else if (test.isTestInProgress) {
                  status = 'in-progress';
                } else if (test.isTestAttemptedByUser) {
                  status = 'completed';
                } else {
                  status = 'available';
                }

                return (
                  <Card
                    key={test._id}
                    className="border border-[var(--q3-stroke-light)] bg-[var(--q3-surface-default)] rounded-lg shadow-none py-0 overflow-hidden q3-interaction-scale-up cursor-pointer"
                    onClick={() =>
                      status === 'not-released'
                        ? showUpcomingTestToast(test)
                        : status === 'completed'
                          ? navigate(`/${packId}/${test._id}/analysis?source=test_listing`)
                          : new Date(test?.endDate) > new Date()
                            ? navigate(`/${packId}/${test._id}/details?source=test_listing`)
                            : showTestEndedToast(test)
                    }
                  >
                    <div className="flex flex-col w-full">
                      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 px-2 lg:px-5 py-2 lg:py-6">
                        <div className="flex items-center w-full lg:w-fit gap-5">
                          <div
                            className={`hidden lg:flex items-center justify-center w-4 h-4 ${status === 'completed' ? 'bg-[var(--q3-success-normal)]' : ''} rounded-full`}
                          >
                            {isPackPurchased ? (
                              status === 'completed' ? (
                                <CheckIcon size={10} weight="bold" color="var(--q3-success-light)" />
                              ) : (
                                <span className="font-medium text-[var(--q3-neutral-light)]">
                                  {index + 1 + (currentPage - 1) * 10}
                                </span>
                              )
                            ) : (
                              <LockIcon size={16} weight="bold" color="var(--q3-danger-normal)" />
                            )}
                          </div>
                          <div className="flex flex-col w-full lg:w-fit">
                            <div className="flex items-start justify-start gap-2 w-full lg:w-fit text-left">
                              <h3 className="font-bold text-md">
                                {test.title}{' '}
                                {test.tags &&
                                  test.tags.map((item, idx) => (
                                    <Badge
                                      key={idx}
                                      style={{
                                        backgroundColor: item.background,
                                        color: item.textColor,
                                        fontSize: '0.65rem', // smaller text
                                        padding: '2px 6px', // tighter padding
                                        borderRadius: '6px', // optional rounded corners
                                      }}
                                      className="leading-none"
                                    >
                                      {item.title}
                                    </Badge>
                                  ))}
                              </h3>
                            </div>

                            <div className="flex items-center flex-wrap gap-2 mt-1 text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                {test.description}
                                {test.isVideoSolutionAvailable && ` • Video solution available`}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex w-full lg:w-fit flex-row gap-2">
                          {status === 'not-released' && (
                            <>
                              <Button
                                className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewSyllabus(test.title, test.syllabus);
                                }}
                              >
                                View Syllabus
                              </Button>
                              <Button className="flex-1 rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-[var(--q3-neutral-default_inverted)] opacity-30 cursor-not-allowed">
                                Attempt Test
                              </Button>
                            </>
                          )}

                          {status === 'available' && (
                            <>
                              <Button
                                className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewSyllabus(test.title, test.syllabus);
                                }}
                              >
                                View Syllabus
                              </Button>
                              <Button
                                className={`flex-1 rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-[var(--q3-neutral-default_inverted)] ${new Date(test?.endDate) < new Date() ? 'opacity-30 cursor-not-allowed' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (new Date(test?.endDate) < new Date()) return;
                                  handleTestNavigation(test._id, 'details');
                                }}
                              >
                                Attempt Test
                              </Button>
                            </>
                          )}

                          {status === 'in-progress' && (
                            <>
                              <Button
                                className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewSyllabus(test.title, test.syllabus);
                                }}
                              >
                                View Syllabus
                              </Button>
                              <Button
                                className={`flex-1 rounded-full bg-[var(--q3-yellow-normal)] hover:bg-[var(--q3-yellow-dark)] text-[var(--q3-neutral-default_inverted)] ${new Date(test?.endDate) < new Date() ? 'opacity-30 cursor-not-allowed' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (new Date(test?.endDate) < new Date()) return;
                                  handleTestNavigation(test._id, 'details');
                                }}
                              >
                                Resume Test
                              </Button>
                            </>
                          )}

                          {status === 'completed' && (
                            <>
                              <Button
                                className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTestNavigation(test._id, 'analysis');
                                }}
                              >
                                View Analysis
                              </Button>
                              <Button
                                className="flex-1 rounded-full bg-[var(--q3-success-normal)] hover:bg-[var(--q3-success-dark)] text-[var(--q3-neutral-default_inverted)]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/${packId}/${test._id}/solutions?categoryId=${selectedCategoryData?.groupId || selectedCategoryData?.title}`,
                                    {
                                      state: { selectedCategoryId: selectedCategoryData?.groupId },
                                    },
                                  );
                                }}
                              >
                                View Solution
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="px-2 lg:pl-14 lg:pr-4 flex justify-between items-center py-2 bg-[var(--q3-surface-dim)] text-[var(--q3-neutral-light)] flex-wrap">
                        {status === 'not-released' && (
                          <div className="flex justify-between items-center w-full flex-wrap gap-3">
                            <div className="flex items-center gap-3 flex-wrap">
                              <div className="flex items-center gap-1 text-sm">
                                <QuestionIcon size={20} /> <strong>{test.totalQuestions} Qs</strong>
                              </div>
                              <div className="flex items-center gap-1 text-sm">
                                <ClockIcon size={20} />
                                <strong>{test.totalTime} Min</strong>
                              </div>
                              <div className="flex items-center gap-1 text-sm">
                                <CheckCircleIcon size={20} /> Score: <strong>{test.totalMarks}</strong>
                              </div>
                            </div>

                            <span className="w-full sm:w-auto flex justify-center sm:justify-end">
                              <Badge className="bg-[var(--q3-surface-default)] border-[var(--q3-stroke-light)] text-[var(--q3-accent-normal)] text-sm font-medium whitespace-nowrap">
                                Releasing: <strong>{formatDateTime(test.startDate)}</strong>
                              </Badge>
                            </span>
                          </div>
                        )}

                        {status === 'in-progress' && (
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1 text-sm">
                              <ClockIcon size={20} /> Time Remaining:
                              <strong>{test.timeLeft} Min</strong>
                            </div>
                          </div>
                        )}
                        {status === 'completed' && (
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1 text-sm">
                              <CheckCircleIcon size={20} /> Score:{' '}
                              <strong>
                                {test.totalMarkScored}/{test.totalMarks}
                              </strong>
                            </div>
                            <div className="flex items-center gap-1 text-sm">
                              <TargetIcon size={20} /> Accuracy: <strong>{test.accuracy?.toFixed(2)}%</strong>
                            </div>
                          </div>
                        )}
                        {status === 'available' && (
                          <div className="flex justify-between items-center w-full flex-wrap gap-3">
                            <div className="flex items-center gap-3 flex-wrap">
                              <div className="flex items-center gap-1 text-sm">
                                <QuestionIcon size={20} /> <strong>{test.totalQuestions} Qs</strong>
                              </div>
                              <div className="flex items-center gap-1 text-sm">
                                <ClockIcon size={20} />
                                <strong>{test.totalTime} Min</strong>
                              </div>
                              <div className="flex items-center gap-1 text-sm">
                                <CheckCircleIcon size={20} /> Score: <strong>{test.totalMarks}</strong>
                              </div>
                            </div>

                            {new Date(test?.endDate) < new Date() && (
                              <span className="w-full sm:w-auto flex justify-center sm:justify-end">
                                <Badge className="bg-[var(--q3-surface-default)] border-[var(--q3-stroke-light)] text-[var(--q3-accent-normal)] text-sm font-medium whitespace-nowrap">
                                  Test Ended On: <strong>{formatDateTime(test?.endDate)}</strong>
                                </Badge>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })
            ) : (
              <EmptyTests />
            )}
          </div>
        )}
        {totalPages > 1 && !loading && (
          <div className="mt-6">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    aria-disabled={currentPage === 1}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 1) handlePageChange(currentPage - 1);
                    }}
                  />
                </PaginationItem>

                {[...Array(totalPages).keys()].map((page) => (
                  <PaginationItem key={page + 1}>
                    <PaginationLink
                      href="#"
                      isActive={currentPage === page + 1}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(page + 1);
                      }}
                    >
                      {page + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    href="#"
                    aria-disabled={currentPage === totalPages}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < totalPages) handlePageChange(currentPage + 1);
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}

        <SyllabusModal
          open={showSyllabusModal}
          onOpenChange={setShowSyllabusModal}
          dialogContent={syllabusModalContent}
        />
      </div>
    </div>
  );
}

export default NonChapterwiseTests;
